// enhanced.js - Non-critical features that can be deferred

/**
 * Scroll Animations Class
 */
class ScrollAnimations {
    constructor() {
        this.init();
    }

    init() {
        this.setupScrollAnimations();
    }

    setupScrollAnimations() {
        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animated');
                    }
                });
            });

            const animateElements = document.querySelectorAll('.animate-on-scroll');
            animateElements.forEach(element => {
                observer.observe(element);
            });
        } else {
            // Fallback for older browsers
            window.addEventListener('scroll', () => {
                const animateElements = document.querySelectorAll('.animate-on-scroll');
                animateElements.forEach(element => {
                    const elementTop = element.offsetTop;
                    const elementBottom = elementTop + element.offsetHeight;
                    const viewportTop = window.pageYOffset;
                    const viewportBottom = viewportTop + window.innerHeight;

                    if (elementBottom > viewportTop && elementTop < viewportBottom) {
                        element.classList.add('animated');
                    }
                });
            });
        }
    }
}

/**
 * Enhanced Site Manager - Non-critical functions
 * This manager handles features that enhance user experience but aren't critical
 * for initial page functionality
 */
class SiteManagerEnhanced {
    constructor() {
        this.functions = {};
        this.initializeEnhanced();
    }

    initializeEnhanced() {
        try {
            // Initialize scroll animations
            this.functions.scrollAnimations = new ScrollAnimations();
            
            // Add more enhanced features here as needed
            // Examples: 
            // - Lazy loading
            // - Image galleries
            // - Advanced tooltips
            // - Analytics tracking
            // - Social sharing
            
            if (window.SITE_CONFIG && window.SITE_CONFIG.features.debugMode) {
                console.log('Enhanced functions initialized');
            }
        } catch (error) {
            console.error('Error initializing enhanced functions:', error);
        }
    }

    getFunction(name) {
        return this.functions[name];
    }

    isLoaded(name) {
        return name in this.functions;
    }
}

/**
 * Do Not Sell / Share opt-out form (CCPA). Progressive enhancement for the
 * /do-not-sell page: POSTs the email to the Express API, which records the
 * suppression. No-ops on any page without the form, so it is safe to init
 * globally.
 */
class DoNotSellForm {
    constructor() {
        this.form = document.getElementById('do-not-sell-form');
        if (!this.form) return;
        this.apiUrl = 'https://api.travelhealthcare.com/api/privacy/do-not-sell';
        this.message = this.form.querySelector('.do-not-sell-message');
        this.init();
    }

    init() {
        // If the browser sends Global Privacy Control, reassure the user it is
        // already honored automatically on submissions.
        if (navigator.globalPrivacyControl) {
            this.setMessage('Your browser is sending a "Do Not Sell" signal, which we honor automatically. You can still submit below to opt out by email.', 'info');
        }
        this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    setMessage(text, type) {
        if (!this.message) return;
        this.message.textContent = text;
        this.message.className = 'do-not-sell-message ' + (type || '');
    }

    async handleSubmit(e) {
        e.preventDefault();
        const emailInput = this.form.querySelector('#dns-email');
        const websiteInput = this.form.querySelector('#dns-website');
        const email = (emailInput && emailInput.value ? emailInput.value : '').trim();
        const website = websiteInput && websiteInput.value ? websiteInput.value : '';
        const button = this.form.querySelector('button[type="submit"]');

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            this.setMessage('Please enter a valid email address.', 'error');
            return;
        }

        if (button) button.disabled = true;
        this.setMessage('Submitting…', 'info');

        try {
            const response = await fetch(this.apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email, website: website })
            });
            const result = await response.json().catch(() => ({}));
            if (response.ok && result.success) {
                this.form.reset();
                this.setMessage('Your request has been recorded. We will stop sharing this email address with recruiters and employers.', 'success');
            } else {
                this.setMessage((result && result.message) || 'Something went wrong. Please email privacy@travelhealthcare.com.', 'error');
            }
        } catch (err) {
            this.setMessage('Could not reach the server. Please email privacy@travelhealthcare.com.', 'error');
        } finally {
            if (button) button.disabled = false;
        }
    }
}

/**
 * Initialize enhanced functions when DOM is ready
 */
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeEnhanced);
} else {
    // DOM already loaded, use a small delay to ensure critical functions loaded first
    setTimeout(initializeEnhanced, 100);
}

function initializeEnhanced() {
    window.SiteManagerEnhanced = new SiteManagerEnhanced();
    new DoNotSellForm();

    // Dispatch custom event to signal enhanced features are loaded
    window.dispatchEvent(new CustomEvent('enhancedFeaturesLoaded', {
        detail: { manager: window.SiteManagerEnhanced }
    }));
}